// Recipe-stub recommendation hook.
//
// Combines three signals to rank stubs:
//   - OCCASION: tags currently in season per activeOccasions(now)
//   - PANTRY:   keyIngredients coverage via computeRecipeMatch
//   - HISTORY:  graduation dedupe (user already has a recipe from
//               this stub) + recency penalty
//
// Returns ranked stubs sorted by score descending, plus the active
// occasion context so callers can render framing copy
// ("FOURTH OF JULY ⋅ in 3 days").
//
// Consumers (v1): the rotating Home banner (ReadyToCookBanner)
// falls through to recommended stubs when no user recipe is
// READY-in-pantry.

import { useMemo } from "react";
import { RECIPE_STUBS } from "../data/recipe_stubs";
import { activeOccasions } from "./occasionContext";
import { computeRecipeMatch } from "./recipePairing";
import { findAllergyConflicts } from "../data/allergens";

// Convert a stub's keyIngredients list into the shape
// computeRecipeMatch expects (a fake `ingredients[]` array of
// { ingredientId } objects). Reuses the same pantry-match math
// already powering the READY badge so we don't reimplement.
function matchStubAgainstPantry(stub, pantry) {
  const fakeIngredients = (stub.keyIngredients || []).map((id) => ({
    ingredientId: id,
  }));
  return computeRecipeMatch({ ingredients: fakeIngredients }, pantry || []);
}

// Set of stub ids the user already has a saved recipe for. Used to
// dedupe — if they've already cooked Classic Cheeseburger via a
// stub-build, don't keep recommending it.
function buildOwnedStubIds(userRecipes) {
  const out = new Set();
  if (!Array.isArray(userRecipes)) return out;
  for (const row of userRecipes) {
    const stubId = row?.recipe?.stubId;
    if (stubId) out.add(stubId);
  }
  return out;
}

export function useRecommendedStubs({
  pantry = [],
  userRecipes = [],
  limit = 5,
  now = null,
  // SAFETY: the viewer's profiles.allergies. Auto-recommendations
  // HARD-EXCLUDE any stub whose ingredients conflict — the app must
  // never *suggest* something the user is allergic to. (Browsable
  // lists warn instead of hiding; this is the one surface that hides.)
  allergies = [],
} = {}) {
  // `now` defaults at render time so callers don't have to pass it.
  // But if a caller wants to mock for testing or push scheduling,
  // they can. The whole result memoizes on (pantry, userRecipes,
  // now) so the ranking doesn't re-run on every render.
  const resolvedNow = now || new Date();
  const nowKey = Math.floor(resolvedNow.getTime() / 60000); // 1-min granularity

  const occasions = useMemo(() => activeOccasions(resolvedNow), [nowKey]);
  const ownedStubIds = useMemo(() => buildOwnedStubIds(userRecipes), [userRecipes]);

  const ranked = useMemo(() => {
    if (occasions.length === 0) return [];
    // Index occasions by tag for O(1) lookups during stub scoring.
    const occByTag = new Map(occasions.map((o) => [o.tag, o]));
    const scored = [];
    for (const stub of RECIPE_STUBS) {
      // Skip if the user already owns a recipe from this stub.
      if (ownedStubIds.has(stub.id)) continue;
      // Allergen hard-exclusion — stub keyIngredients (canonical-id
      // slugs; underscores become spaces so "canadian_bacon" matches
      // text terms) vs the viewer's allergy list. The prefix matcher
      // errs toward exclusion, which is the right direction here.
      if (Array.isArray(allergies) && allergies.length > 0) {
        const hay = [
          stub.title || "",
          ...(stub.keyIngredients || []).map((k) => String(k).replace(/_/g, " ")),
        ].join(" , ");
        if (findAllergyConflicts(hay, allergies).length > 0) continue;
      }
      // Find intersection between stub.discoveryTags and active
      // occasions. If no intersection, this stub isn't relevant
      // right now.
      const matchedOccs = (stub.discoveryTags || [])
        .map((t) => occByTag.get(t))
        .filter(Boolean);
      if (matchedOccs.length === 0) continue;

      // Score: sum of occasion weights + pantry-match bonus.
      let score = 0;
      for (const occ of matchedOccs) score += occ.weight;

      // Pantry boost — scaled coverage. Stubs the user can almost
      // entirely make from their pantry rank higher. The bonus is
      // capped so a perfectly-pantry-matched obscure stub doesn't
      // beat a moderate match on a strong-occasion stub.
      const m = matchStubAgainstPantry(stub, pantry);
      if (m.total > 0) {
        const coverage = (m.pairedCount + m.substituteCount * 0.7 + m.wrongStateCount * 0.5) / m.total;
        score += coverage * 30;
      }

      scored.push({
        stub,
        match: m,
        occasions: matchedOccs,
        score,
      });
    }
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, limit);
  }, [occasions, ownedStubIds, pantry, limit, allergies]);

  return {
    stubs: ranked,
    occasions,
    loading: false, // synchronous — no async source
  };
}
